import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, Grid, Paper, Skeleton, Typography } from '@mui/material';
import routes from 'constants/routes';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import { CashTotalsOverview, formatCashAmount } from '../BranchCashSummary';
import { useBranchReceiptsTotals } from '../../hooks/useBranchReceiptsTotals';

/**
 * Vista agrupadora de todas las cajas: totales con un fetch único a
 * `data/sales/receipts/totals`. Cada tarjeta linkea a `/cajas/:branchSlug`.
 */
export function CashBoxesOverview(): JSX.Element {
	const { data, loading, error, cachedAt } = useBranchReceiptsTotals();

	if (loading) {
		return (
			<Grid container spacing={2}>
				{Array.from({ length: 6 }).map((_, idx) => (
					<Grid item xs={12} md={6} key={idx}>
						<Skeleton variant="rectangular" height={150} />
					</Grid>
				))}
			</Grid>
		);
	}

	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}

	const branches = data?.branches ?? [];
	const grandTotals = data?.grandTotals;

	return (
		<>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
				{grandTotals && (
					<Box display="flex" gap={1} flexWrap="wrap">
						<Chip label={`${grandTotals.branches} cajas`} size="small" variant="outlined" />
						<Chip
							label={`${grandTotals.total.toLocaleString('es-AR')} recibos sin depositar`}
							size="small"
							variant="outlined"
						/>
						<Chip
							label={`Total: ${formatCashAmount(grandTotals.amountInMainCurrency, 'USD')}`}
							size="small"
							color="primary"
						/>
					</Box>
				)}
				<CachedBadge cachedAt={cachedAt} />
			</Box>

			{branches.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No hay recibos por depositar en ninguna caja 🙌
				</Typography>
			) : (
				<Grid container spacing={2}>
					{branches.map((item) => (
						<Grid item xs={12} md={6} key={item.slug}>
							<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
								<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
									<Typography variant="subtitle1" fontWeight={600}>
										{item.branch}
									</Typography>
									<Button
										size="small"
										variant="outlined"
										component={RouterLink}
										to={routes.branchCash.detail(item.slug)}
										sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
									>
										Ver detalle
									</Button>
								</Box>
								<CashTotalsOverview
									totalReceipts={item.total}
									totalAmount={item.amountInMainCurrency}
									byCurrency={item.byTransactionCurrency}
								/>
							</Paper>
						</Grid>
					))}
				</Grid>
			)}
		</>
	);
}
